import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, X, Search, Loader2, Users, Wallet } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { Input } from "../ui/input";

/**
 * Picker used at the top of the Job form to link a job to a customer.
 * - Search-as-you-type by code / name / email
 * - Clicking a result populates job.customer_id + copies contact fields into the client_* fields
 * - "Walk-in / COD" removes the link (keeps client fields as-is)
 */
export const CustomerPicker = ({ form, setForm }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [attached, setAttached] = useState(null);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Load attached customer info when customer_id changes
  useEffect(() => {
    if (!form.customer_id) { setAttached(null); return; }
    if (attached?.id === form.customer_id) return;
    (async () => {
      try {
        const c = await adminApi.getCustomer(form.customer_id);
        setAttached(c);
      } catch { /* silent — will be re-attempted */ }
    })();
    // eslint-disable-next-line
  }, [form.customer_id]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const rows = await adminApi.listCustomers({ q: q.trim(), active: true });
        setResults(rows.slice(0, 8));
      } catch { /* silent */ }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  // click-outside
  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const attach = (c) => {
    setForm((f) => ({
      ...f,
      customer_id: c.id,
      customer_code: c.code,
      client_name: c.name || f.client_name,
      client_id_number: c.id_or_reg_number || f.client_id_number,
      contact_mobile: c.mobile || f.contact_mobile,
      email: c.email || f.email,
      contact_home: c.telephone || f.contact_home,
      // For company, prefill insurance-on-file into the insurance block if empty
      insurance_company: f.insurance_company || c.insurer_on_file || "",
      policy_number: f.policy_number || c.policy_number_on_file || "",
      broker: f.broker || c.broker_on_file || "",
    }));
    setAttached(c);
    setOpen(false);
    setQ("");
    toast.success(`Linked ${c.code} — ${c.name}`);
  };

  const detach = () => {
    setForm((f) => ({ ...f, customer_id: null, customer_code: null }));
    setAttached(null);
  };

  return (
    <div className="bg-white border border-[#cfd7db] rounded-md p-5 shadow-sm" data-testid="customer-picker" ref={boxRef}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Customer account</div>
          <div className="font-display font-bold text-[#111619] mt-0.5">Attach an account or leave as walk-in / COD</div>
        </div>
        <Link
          to="/admin/customers/new"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-[#f28a16] hover:underline inline-flex items-center gap-1"
        >
          <UserPlus size={12} /> Create customer in new tab
        </Link>
      </div>

      {attached ? (
        <div className="mt-4 flex items-center justify-between gap-3 bg-[#f5f6f6] border border-[#cfd7db] rounded-md p-3" data-testid="customer-attached">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold text-[#f28a16]">{attached.code}</span>
              <span className="font-display font-bold text-[#111619] truncate">{attached.name}</span>
              <span className="bg-[#edf2f4] text-[#3f4b52] text-xs font-display uppercase tracking-widest px-1.5 py-0.5 rounded-sm">
                {attached.customer_type === "company" ? "Company" : attached.customer_type === "cod" ? "COD" : "Individual"}
              </span>
            </div>
            <div className="text-xs text-[#3f4b52] mt-0.5 truncate">
              {[attached.email, attached.mobile, attached.vat_number && `VAT ${attached.vat_number}`].filter(Boolean).join(" · ") || "—"}
            </div>
          </div>
          <button
            type="button"
            onClick={detach}
            className="text-[#dc2626] text-xs inline-flex items-center gap-1 hover:underline"
            data-testid="customer-detach"
          >
            <X size={12} /> Detach
          </button>
        </div>
      ) : (
        <div className="mt-4 relative">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa6ad]" />
            <Input
              value={q}
              onFocus={() => setOpen(true)}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              placeholder="Search by customer code, name or email…"
              className="pl-9 bg-white border-[#cfd7db] h-10"
              data-testid="customer-picker-search"
            />
          </div>
          {open && q.trim() && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[#cfd7db] rounded-md shadow-lg max-h-72 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-sm text-[#3f4b52] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Searching…</div>
              ) : results.length === 0 ? (
                <div className="p-4 text-sm text-[#3f4b52]">No matches. <Link to="/admin/customers/new" target="_blank" className="text-[#f28a16] hover:underline">Create new customer →</Link></div>
              ) : (
                <ul className="divide-y divide-[#eef2f4]">
                  {results.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => attach(c)}
                        data-testid={`customer-picker-option-${c.code}`}
                        className="w-full text-left px-3 py-2.5 hover:bg-[#f5f6f6] flex items-start justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-[#f28a16]">{c.code}</span>
                            <span className="font-display font-bold text-[#111619] truncate">{c.name}</span>
                          </div>
                          <div className="text-xs text-[#3f4b52] truncate">
                            {[c.email, c.mobile].filter(Boolean).join(" · ") || "—"}
                          </div>
                        </div>
                        <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52] inline-flex items-center gap-1 shrink-0">
                          {c.customer_type === "cod" ? <Wallet size={10} /> : <Users size={10} />}
                          {c.customer_type === "company" ? "Company" : c.customer_type === "cod" ? "COD" : "Individual"}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <p className="mt-2 text-[11px] text-[#9aa6ad]">
            No account required — you can leave this empty for a once-off / walk-in job. The client name field below is still used.
          </p>
        </div>
      )}
    </div>
  );
};

export default CustomerPicker;
